/**
 * TK-48 §2 (lee chọn theo hẳn bản ChatGPT v06) — KHUNG ĐIỀU KHIỂN của đối tượng đang chọn.
 *   · `noi` (≥ 900 px): nổi góc phải TRÊN bản đồ. `truot` (< 900 px): TẤM TRƯỢT nổi ở nửa dưới màn, bật lên ngay khi chạm đèn / tủ.
 *   · Đầu khung: tên · chip loại (Đèn / Tủ / Tuyến) · "Thuộc tủ …" · nút đóng. Hai thẻ Độ sáng / Nguồn AC. Mục gập "Thông số <đèn>".
 *   · Tấm trượt: tay nắm kéo thấp còn MỘT hàng hoặc cao tới 70 % màn (`caoTamTruot`). Cử chỉ trên tay nắm `touch-action: none`
 *     ⇒ không kéo trang, không kéo-để-tải-lại (luật TK-45 §1); thân tấm `overscroll-behavior: contain`.
 *   · Vật đang chọn KHÔNG BAO GIỜ bị khung che: trang đo bbox khung (ref) và `panInside` theo `vungTuDo`.
 */
import { useRef, useState, type ReactNode, type Ref } from 'react';
import { ChevronDown, Lightbulb, Power, SlidersHorizontal, X } from 'lucide-react';
import { CAO_THAP, caoTamTruot } from '../../lib/dieuKhienDoSang';

export type TheKhung = 'do-sang' | 'nguon-ac';

export function KhungDieuKhien({ kieu, ten, loai, thuoc, onDong, theDim, theAc, thongSo, khungRef, the, setThe }: {
  kieu: 'noi' | 'truot';
  ten: string; loai: string; thuoc?: string | null;
  onDong: () => void;
  /** null ⇒ không có thẻ Độ sáng (cờ NEMA tắt / chưa có đèn) */
  theDim: ReactNode | null;
  theAc: ReactNode;
  thongSo?: { ten: string; node: ReactNode } | null;
  khungRef?: Ref<HTMLElement>;
  the: TheKhung; setThe: (t: TheKhung) => void;
}) {
  const coDim = theDim !== null;
  const theHien: TheKhung = coDim ? the : 'nguon-ac';
  /* tấm trượt: null = cao theo nội dung (trần 70 % màn); số = người đã kéo */
  const [cao, setCao] = useState<number | null>(null);
  const keo = useRef<{ y: number; h: number } | null>(null);
  const goc = useRef<HTMLElement | null>(null);
  const datRef = (el: HTMLElement | null) => {
    goc.current = el;
    if (typeof khungRef === 'function') khungRef(el);
    else if (khungRef && typeof khungRef === 'object') (khungRef as { current: HTMLElement | null }).current = el;
  };
  const thap = kieu === 'truot' && cao === CAO_THAP;
  const tayNam = kieu === 'truot' && (
    <div className="op-tay-nam" data-o="tay-nam" role="button" tabIndex={0} aria-label={thap ? 'Mở rộng khung điều khiển' : 'Thu gọn khung điều khiển'}
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); keo.current = { y: e.clientY, h: goc.current?.getBoundingClientRect().height ?? CAO_THAP }; }}
      onPointerMove={(e) => { if (!keo.current) return; e.preventDefault(); setCao(Math.max(CAO_THAP, keo.current.h + (keo.current.y - e.clientY))); }}
      onPointerUp={(e) => {
        const k = keo.current; keo.current = null; if (!k) return;
        const moi = k.h + (k.y - e.clientY);
        /* chạm (không kéo) ⇒ đổi thấp ⇄ theo nội dung */
        if (Math.abs(k.y - e.clientY) < 6) setCao(thap ? null : CAO_THAP);
        else setCao(caoTamTruot(moi, window.innerHeight));
      }}
      onPointerCancel={() => { keo.current = null; }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCao(thap ? null : CAO_THAP); } }}>
      <span aria-hidden="true" />
    </div>
  );
  return (
    <section ref={datRef} className={kieu === 'noi' ? 'op-khung op-khung-noi' : 'op-khung op-tam-truot'} data-o="khung-dieu-khien" data-kieu={kieu}
      data-thap={thap ? '1' : undefined} data-keo={kieu === 'truot' && cao !== null ? '1' : undefined} aria-label="Điều khiển đối tượng đang chọn"
      style={kieu === 'truot' && cao !== null ? { height: cao } : undefined}>
      {tayNam}
      <header className="op-khung-dau">
        <div className="op-khung-ten">
          <h2 title={ten}>{ten}</h2>
          <span className="op-chip-loai" data-o="chip-loai">{loai}</span>
        </div>
        {thuoc && <small data-o="thuoc-tu">Thuộc tủ {thuoc}</small>}
        <button type="button" className="op-khung-dong" onClick={onDong} aria-label="Đóng khung điều khiển" data-o="dong-khung"><X className="h-[18px] w-[18px]" /></button>
      </header>
      {!thap && (
        <>
          <div className="op-the" role="tablist" aria-label="Loại điều khiển" data-o="the-dieu-khien">
            {coDim && (
              <button type="button" role="tab" aria-selected={theHien === 'do-sang'} onClick={() => setThe('do-sang')} data-the="do-sang">
                <SlidersHorizontal className="h-[15px] w-[15px]" aria-hidden="true" />Độ sáng
              </button>
            )}
            <button type="button" role="tab" aria-selected={theHien === 'nguon-ac'} onClick={() => setThe('nguon-ac')} data-the="nguon-ac">
              <Power className="h-[15px] w-[15px]" aria-hidden="true" />Nguồn AC
            </button>
          </div>
          <div className="op-khung-than" data-o="than-khung">
            {theHien === 'do-sang' ? theDim : theAc}
            {thongSo && (
              <details className="op-thong-so" data-o="thong-so-den" open={kieu === 'noi'}>
                <summary><Lightbulb className="h-[14px] w-[14px]" aria-hidden="true" />Chi tiết {thongSo.ten}<ChevronDown className="h-[14px] w-[14px]" aria-hidden="true" /></summary>
                {thongSo.node}
              </details>
            )}
          </div>
        </>
      )}
    </section>
  );
}
